import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Loader2, Check, Sparkles, Building2 } from "lucide-react";
import { toast } from "sonner";
import EnterpriseContactDialog from "@/components/EnterpriseContactDialog";

const PLANS = [
  { id: "solo", name: "Solo", price: "$0", tag: "Forever free", feats: ["1 project", "100 feedback / mo", "7-day history"] },
  { id: "team", name: "Team", price: "$39", tag: "/mo", feats: ["Unlimited projects", "5 teammates", "Integrations", "Unlimited history"] },
  { id: "business", name: "Business", price: "$149", tag: "/mo", feats: ["Everything in Team", "Unlimited teammates", "White-label", "Roles & support"] },
];
const RANK = { solo: 0, team: 1, business: 2 };

export default function Billing() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState("");
  const [checking, setChecking] = useState(false);

  const loadPlan = useCallback(() => {
    apiClient.get("/org").then(({ data }) => setPlan(data.org?.plan || "solo")).catch(() => setPlan("solo"));
  }, []);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 6) { setChecking(false); toast.error("Still processing — check back in a moment."); return; }
    try {
      const { data } = await apiClient.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setChecking(false);
        toast.success(`You're on the ${data.plan_id} plan now! 🚢`);
        loadPlan();
        return;
      }
      if (data.status === "expired") { setChecking(false); toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch {
      setChecking(false);
      toast.error("Couldn't verify payment status.");
    }
  }, [loadPlan]);

  useEffect(() => {
    loadPlan();
    const sid = params.get("session_id");
    if (sid) {
      setChecking(true);
      pollStatus(sid);
      setParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upgrade = async (planId) => {
    setLoadingPlan(planId);
    try {
      // Preview the change first (prorated if they already have a live subscription).
      let proceed = true;
      try {
        const { data: pv } = await apiClient.get(`/billing/preview/${planId}`);
        if (pv.mode === "proration") {
          const verb = pv.direction === "downgrade" ? "downgrade" : "upgrade";
          proceed = window.confirm(
            `You're about to ${verb} to ${planId}. You'll be charged $${pv.amount_due_now} today (prorated), then $${pv.recurring}/mo at renewal. Continue?`
          );
        }
      } catch { /* preview is best-effort; continue to checkout */ }
      if (!proceed) { setLoadingPlan(""); return; }

      const { data } = await apiClient.post("/billing/checkout", { plan_id: planId, origin_url: window.location.origin });
      if (data.updated) {
        toast.success(`You're on the ${data.plan} plan now! 🚢`);
        loadPlan();
        setLoadingPlan("");
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoadingPlan("");
    }
  };

  return (
    <div>
      <div className="h-20 flex items-center px-6 sm:px-8">
        <div>
          <h1 className="font-display text-3xl font-700 tracking-tight">Billing</h1>
          <p className="text-sm text-ink/60">Manage your workspace plan.</p>
        </div>
      </div>

      <div className="p-6 lg:p-8 pt-2 max-w-5xl">
        {checking && (
          <div className="nb-card bg-sunny p-4 mb-6 flex items-center gap-3" data-testid="billing-verifying">
            <Loader2 className="h-5 w-5 animate-spin" /> <span className="font-700 text-sm">Verifying your payment…</span>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch" data-testid="billing-plans">
          {PLANS.map((p) => {
            const current = plan === p.id;
            return (
              <div key={p.id} className={`nb-card p-6 flex flex-col ${current ? "bg-sunny" : "bg-white"}`} data-testid={`billing-plan-${p.id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-700 text-xl">{p.name}</h3>
                  {current && <span className="text-[10px] font-800 uppercase px-2 py-1 rounded-full border-2 border-ink bg-grass text-white">Current</span>}
                </div>
                <div className="flex items-end gap-1 mt-2 mb-4">
                  <span className="font-display text-4xl font-700">{p.price}</span>
                  <span className="text-sm text-ink/60 font-700 mb-1.5">{p.tag}</span>
                </div>
                <ul className="space-y-2 flex-1 mb-5">
                  {p.feats.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm font-700">
                      <span className="h-4 w-4 bg-white border-2 border-ink rounded flex items-center justify-center shrink-0"><Check className="h-2.5 w-2.5" /></span>{f}
                    </li>
                  ))}
                </ul>
                {p.id === "solo" ? (
                  <button disabled className="nb-btn bg-white w-full py-2.5 text-sm opacity-50">Free tier</button>
                ) : current ? (
                  <button disabled className="nb-btn bg-grass text-white w-full py-2.5 text-sm opacity-80">Active plan</button>
                ) : (
                  <button onClick={() => upgrade(p.id)} disabled={!!loadingPlan} data-testid={`upgrade-${p.id}`}
                    className="nb-btn bg-orange text-white w-full py-2.5 text-sm disabled:opacity-60">
                    {loadingPlan === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : `${RANK[plan] > RANK[p.id] ? "Switch to" : "Upgrade to"} ${p.name}`}
                  </button>
                )}
              </div>
            );
          })}

          <div className="nb-card bg-ink text-cream p-6 flex flex-col" data-testid="billing-plan-enterprise">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-orange" />
              <h3 className="font-display font-700 text-xl">Enterprise</h3>
            </div>
            <div className="flex items-end gap-1 mt-2 mb-4">
              <span className="font-display text-4xl font-700">Custom</span>
            </div>
            <ul className="space-y-2 flex-1 mb-5 text-cream/90">
              {["AI feedback intelligence (soon)", "SSO / SAML / SCIM (soon)", "Audit logs & SLA", "Dedicated support"].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm font-700">
                  <span className="h-4 w-4 bg-orange border-2 border-ink rounded flex items-center justify-center shrink-0"><Sparkles className="h-2.5 w-2.5 text-white" /></span>{f}
                </li>
              ))}
            </ul>
            <EnterpriseContactDialog triggerClassName="nb-btn bg-orange text-white w-full py-2.5 text-sm" triggerLabel="Talk to sales" />
          </div>
        </div>

        <p className="text-xs font-700 text-ink/40 mt-6">Payments are processed securely by Stripe. Test mode is active during beta.</p>
      </div>
    </div>
  );
}
